import { useEffect, useRef } from 'react';

import { useRouter } from 'react-resource-router';

import { getDocument } from '@atlaskit/browser-apis';

import {
	CLI_ROUTE_NAME,
	HOME_ROUTE_NAME,
	type MicrositeRouteName,
} from '@atlassian/twg-microsite-experience/routes';

/**
 * The `<title>` each route's HTML document ships with.
 *
 * These duplicate the `<title>` in `src/index.html` and `src/cli/index.html`,
 * which remain the source of truth: they are what a crawler or unfurler reads,
 * and they are what the browser shows on a full page load. The duplication is
 * guarded by `src/__tests__/html-metadata.test.ts`, which fails if this map and a
 * document disagree.
 */
export const ROUTE_TITLES: Record<MicrositeRouteName, string> = {
	[HOME_ROUTE_NAME]: 'Atlassian Teamwork Graph',
	[CLI_ROUTE_NAME]: 'Atlassian Teamwork Graph CLI',
};

const isMicrositeRouteName = (name: string | undefined): name is MicrositeRouteName =>
	name === HOME_ROUTE_NAME || name === CLI_ROUTE_NAME;

/**
 * Keeps the browser tab title in step with the route.
 *
 * Each route serves its own HTML document, so a full page load already arrives
 * with the right `<title>`. An SPA navigation does not: `NavButtons` and the
 * home page's CLI call to action both call `preventDefault()` and push the route,
 * which swaps the rendered page without fetching the destination document. The
 * `<head>` therefore keeps belonging to whichever document was loaded first, and
 * without this hook `/cli` reached by clicking would show the home page's title.
 *
 * Only the title is synchronised. `canonical` and the `og:*`/`twitter:*` tags are
 * deliberately left alone: search engines and social unfurlers fetch each URL
 * directly and never see the result of an in-app navigation, so rewriting them
 * client-side would add moving parts without changing what any crawler reads.
 *
 * The first render is intentionally a no-op, since the document already carries
 * the correct title for the route that was loaded.
 */
export const useDocumentTitleOnRouteChange = (): void => {
	const [{ route }] = useRouter();
	const isFirstRender = useRef(true);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		// An unknown route resolves to the catch-all, which redirects to `/`. Leaving
		// the title alone means the tab keeps the previous route's title for the one
		// render that pass takes, rather than flashing something wrong.
		const routeName = route?.name;
		if (!isMicrositeRouteName(routeName)) {
			return;
		}

		const doc = getDocument();
		if (doc) {
			doc.title = ROUTE_TITLES[routeName];
		}
	}, [route?.name]);
};
